import { StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";
import App from "./App";
import { installErrorReporter, send } from "./lib/bridge";
import { installStaleChunkReload } from "./lib/stale-chunk";
import "./styles/index.css";

installErrorReporter();
// 部署后旧页面拿不到懒加载分块：刷新一次拿新入口（lib/stale-chunk.ts）
installStaleChunkReload();

const boot = document.getElementById("boot");

/**
 * 挂载失败必须留下可见痕迹。
 *
 * 白屏的本质是「出错了但没人说」——所以这里不吞异常：
 * 页面上直接写出错误，同时 postMessage 通知宿主显示重试按钮。
 */
function fatal(message: string, stack?: string): void {
  if (boot) {
    boot.textContent = `Failed to load / 配置台加载失败：${message}`;
    boot.style.color = "#b91c1c";
  }
  send({ type: "admin:error", message, stack });
}

let root: Root | null = null;

function mount(): void {
  // 被 wujie 承载时 document 是代理过的，每次挂载都要重新取 #root
  const el = document.getElementById("root");
  if (!el) {
    fatal("页面结构缺少 #root 挂载点");
    return;
  }
  try {
    root = createRoot(el);
    root.render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
    // 只有渲染真正跑起来才撤掉兜底层
    document.getElementById("boot")?.remove();
  } catch (e) {
    const err = e as Error;
    fatal(err.message, err.stack);
  }
}

mount();

/*
 * 关于 wujie 的 __WUJIE_MOUNT / __WUJIE_UNMOUNT 生命周期钩子：**刻意没有导出**。
 *
 * 导出它们本可以让 wujie 复用 sandbox（其 shadow.js：
 * `isRebuildMode = !alive && !isFunction(__WUJIE_MOUNT)`），重新激活时省掉
 * 一整轮 iframe 重建与脚本重新执行。
 *
 * 但 2026-08-14 实测三种写法都注册不上 —— 挂 window、挂
 * __WUJIE_RAW_WINDOW__、按 __POWERED_BY_WUJIE__ 分支，事后从宿主 window
 * 和 iframe.contentWindow 上都读不到。推测与 Vite 产物是单个
 * `<script type="module">` 有关（wujie 的脚本沙箱主要针对传统脚本），
 * **未经确认**。
 *
 * 现状不影响正确性：应用每次都能正常挂载，单实例 mount→ready 约 350~780ms。
 * 真要拿这部分性能，得先查清 wujie 对 ESM 产物的处理方式，别照抄网上的写法。
 */
